import {
  LocalOfferLocation,
  LocalOfferMarker,
} from '@varo/graphql-schema-types';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  MapCameraChangedEvent,
  MapCameraProps,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';
import { useRouter } from 'expo-router';
import {
  PlaceDetailsResponse,
  RewardsLocation,
  RewardsMapResponse,
  RewardsMapType,
  RewardsMapViewProps,
} from 'features/rewards/components/RewardsMap/RewardsMapView.helpers';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import MapLoadingIndicator from 'shared/components/maps/MapLoadingIndicator/MapLoadingIndicator';
import RequestLocationBottomSheet from 'shared/components/maps/RequestLocationBottomSheet/RequestLocationBottomSheet';
import Icon, { IconKeyType } from 'shared/components/vxl/icon/Icon/Icon';
import VXLSearchField from 'shared/components/vxl/input/VXLSearchField/VXLSearchField';
import MapNode, { MapNodeProps } from 'shared/components/vxl/MapNode/MapNode';
import { NavigationBar } from 'shared/components/vxl/navigation/NavigationBar/NavigationBar';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useClusteredMarkers from 'shared/hooks/maps/useClusteredMarkers';
import useMarkerById from 'shared/hooks/maps/useMarkerById';
import useZipCodeCoordinates from 'shared/hooks/maps/useZipCodeCoordinates';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import theme from 'shared/styles/theme';
import getEnvironmentVar from 'shared/utils/getEnvironmentVar';
import fetchPredictionDetails from 'shared/utils/maps/fetchPredictionDetails';
import fetchPredictions from 'shared/utils/maps/fetchPredictions';
import getMarkerZIndex from 'shared/utils/maps/getMarkerZIndex';
import {
  DefaultMapZoom,
  WebMapControlProps,
} from 'shared/utils/maps/mapConstants';

const MARKER_ICON_MAP: Record<LocalOfferMarker, IconKeyType> = {
  [LocalOfferMarker.Merchant]: 'STORE',
  [LocalOfferMarker.Food]: 'FOOD',
  [LocalOfferMarker.Entertainment]: 'ENTERTAINMENT',
  [LocalOfferMarker.Travel]: 'TRAVEL',
  [LocalOfferMarker.Shopping]: 'SHOPPING',
  [LocalOfferMarker.UndefinedMarker]: 'location',
  [LocalOfferMarker.Gas]: 'GAS',
};

const isLocalOfferLocation = (
  loc: RewardsLocation,
): loc is LocalOfferLocation => {
  return 'marker' in loc && loc.marker !== undefined;
};

const getMarkerIcon = (marker: RewardsLocation): IconKeyType => {
  if (isLocalOfferLocation(marker)) {
    return MARKER_ICON_MAP[marker.marker] ?? 'location';
  }
  return 'location';
};
export default function RewardsMapView<T extends RewardsLocation>({
  id,
  navigationBarTitle,
  onRewardsLocationSelected,
  isLoading = false,
  markerData = [],
  onLocationChange,
  onMarkerPress,
  requestLocationTitle,
  requestLocationBody,
  showMapSearchBar = true,
  onSearchValueChange,
  shouldShowSearchThisAreaButtonVisible = true,
  selectedMarkerId: externalSelectedMarkerId,
  defaultCameraLocation,
  defaultCameraZoom,
  onMapTap,
  onCameraLocationChange,
}: RewardsMapViewProps<T>) {
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'rewardsMap',
  });
  const router = useRouter();
  const getZipCodeCoordinates = useZipCodeCoordinates();
  const { currentEnv, getMapsUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);
  const mapsUrl = getMapsUrl(currentEnv);
  const [isSearchThisAreaButtonVisible, setIsSearchThisAreaButtonVisible] =
    useState(false);

  // Tracks only user-driven interactions: panning, search, GPS grant
  const [internalCameraLocation, setInternalCameraLocation] = useState<
    MapCameraProps | undefined
  >();

  // using mapId from google cloud config to enable custom map styling, with fallback to default map if not set
  const mapId = getEnvironmentVar('EXPO_CASHBACK_MAP_ID') ?? id;

  // Derived directly from prop during render
  const externalCameraLocation: MapCameraProps | undefined =
    defaultCameraLocation
      ? { center: defaultCameraLocation, zoom: defaultCameraZoom ?? 13 }
      : undefined;

  // Internal (user interaction) takes precedence; falls back to parent-provided position
  const cameraLocation = internalCameraLocation ?? externalCameraLocation;

  const [searchLocation, setSearchLocation] =
    useState<google.maps.LatLngLiteral>();

  const [search, setSearch] = useState('');
  const [RewardsMaps, setRewardsMaps] = useState<RewardsMapType[]>([]);
  const [isRewardsMapsLoading, setIsRewardsMapsLoading] = useState(false);
  const isInitialLoad = useRef(true);
  const [selectedAddress, setSelectedAddress] = useState('');

  const mapMarkerToMapNodeProps = (
    marker: RewardsLocation,
    isSelected: boolean,
    onPress: () => void,
  ): MapNodeProps => ({
    iconKey: getMarkerIcon(marker),
    metric: isLocalOfferLocation(marker) ? (marker.cashbackAmount ?? '0') : '0',
    labelText: marker.name ?? '',
    priceText: '',
    selected: isSelected,
    analyticsId: marker.id,
    onPress,
  });

  const addressSelectedEvent = usePressableAnalytics({
    analyticsId: 'addressSelected',
    customAttributes: { address_selected: search },
  });

  useEffect(() => {
    if (selectedAddress) {
      addressSelectedEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress]);

  const findAtmSearchAreaEvent = usePressableAnalytics({
    analyticsId: 'findAtmSearchClicked',
  });

  const handleLocation = useCallback(
    (position: GeolocationPosition) => {
      if (defaultCameraLocation) {
        return;
      }
      const newCoords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      const initialCamera = {
        center: newCoords,
        zoom: 13,
      };

      setInternalCameraLocation(initialCamera);
      onLocationChange(newCoords, true);
    },
    [defaultCameraLocation, onLocationChange],
  );
  const handleDeniedLocation = useCallback(async () => {
    if (defaultCameraLocation) {
      return;
    }
    try {
      const newCoords = await getZipCodeCoordinates(mapsUrl);

      if (newCoords) {
        setInternalCameraLocation({
          center: newCoords,
          zoom: 13,
        });
        onLocationChange(newCoords, true);
      }
    } catch (e) {
      if (e instanceof AbortError) {
        return;
      }

      console.warn('Error fetching zip: ', e);
    }
  }, [defaultCameraLocation, getZipCodeCoordinates, mapsUrl, onLocationChange]);

  const handleValueChange = async (value: string) => {
    setIsRewardsMapsLoading(true);
    setSearch(value);

    // Don't make an API request if we don't have any search value
    if (value.trim() === '') {
      setIsRewardsMapsLoading(false);
      return;
    }

    try {
      const request = await fetchPredictions(value, mapsUrl);
      const RewardsMapResponse = (await request.json()) as RewardsMapResponse;

      if (RewardsMapResponse) {
        setRewardsMaps(RewardsMapResponse.RewardsMaps);
      }
    } catch (e) {
      console.error('Error fetching RewardsMaps: ', e);
    } finally {
      setIsRewardsMapsLoading(false);
    }
    onSearchValueChange?.(value);
  };

  const handleRewardsMapTapped = async (RewardsMap: RewardsMapType) => {
    setSearch(RewardsMap.description);
    setSelectedAddress(RewardsMap.description);

    if (RewardsMap) {
      try {
        const request = await fetchPredictionDetails(
          RewardsMap.place_id,
          mapsUrl,
        );

        const placeDetailsResponse =
          (await request.json()) as PlaceDetailsResponse;

        if (placeDetailsResponse) {
          const newLat = parseFloat(
            placeDetailsResponse?.result?.geometry?.location?.lat,
          );
          const newLng = parseFloat(
            placeDetailsResponse?.result?.geometry?.location?.lng,
          );

          const newCoords = { lat: newLat, lng: newLng };

          setInternalCameraLocation({
            center: newCoords,
            zoom: 13,
          });

          await onRewardsLocationSelected(placeDetailsResponse);
        }
      } catch (e) {
        console.error('Error fetching RewardsMap details: ', e);
      }
    }
  };
  const handleCameraChange = useCallback(
    (ev: MapCameraChangedEvent) => {
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        return;
      }

      setIsSearchThisAreaButtonVisible(true);
      setSearchLocation(ev.detail.center);
      setInternalCameraLocation(ev.detail);
      onCameraLocationChange?.(ev.detail.center, ev.detail.zoom);
    },
    [onCameraLocationChange],
  );

  const handleSearchThisAreaPress = () => {
    findAtmSearchAreaEvent();
    setIsSearchThisAreaButtonVisible(false);
    if (searchLocation?.lat && searchLocation?.lng) {
      onLocationChange(
        {
          lat: searchLocation?.lat,
          lng: searchLocation?.lng,
        },
        false,
      );
    }
  };
  const apiKey = getEnvironmentVar('EXPO_GOOGLE_MAPS_WEB_API_KEY');
  if (typeof apiKey !== 'string' || !apiKey.length) {
    console.warn('Missing EXPO_GOOGLE_MAPS_WEB_API_KEY');
  }

  const handleMapClick = (event: MapMouseEvent) => {
    // If the click event has a placeId, it means a marker or point of interest was clicked, so we don't treat it as a map background tap
    if (!!event?.detail?.placeId) {
      return;
    }

    if (onMapTap) {
      onMapTap();
    }
  };

  const clusteredMarkers = useClusteredMarkers(markerData ?? []);

  const originalMarkerById = useMarkerById(markerData);

  return (
    <View id={id} style={styles.container}>
      {navigationBarTitle.length > 0 ? (
        <NavigationBar
          title={navigationBarTitle}
          primaryIcon={<Icon iconKey="BACK" iconSize="MD" />}
          primaryOnPress={() => router.back()}
          accessory={
            showMapSearchBar ? (
              <VXLSearchField
                options={RewardsMaps.map((item) => ({
                  key: item.place_id,
                  label: item.description,
                  value: item,
                }))}
                onSelectOption={handleRewardsMapTapped}
                placeholder={translatedText('searchPlaceholder')}
                onChangeText={handleValueChange}
                value={search}
                isLoading={isRewardsMapsLoading}
                analyticsId="atmSearch"
              />
            ) : null
          }
        />
      ) : null}
      <View style={styles.mapContainer}>
        {!cameraLocation || isLoading ? <MapLoadingIndicator /> : null}
        {cameraLocation ? (
          <APIProvider apiKey={apiKey as string}>
            <Map
              {...cameraLocation}
              defaultZoom={DefaultMapZoom.l}
              mapId={mapId}
              reuseMaps={true}
              {...WebMapControlProps}
              onCameraChanged={handleCameraChange}
              onClick={handleMapClick}
            >
              {clusteredMarkers.map((marker) => {
                const isSelected = externalSelectedMarkerId === marker.id;
                const hasHiddenIcon = (marker as any).hideIcon;

                return (
                  <AdvancedMarker
                    key={marker.id}
                    position={{
                      lat: marker.coordinate.latitude,
                      lng: marker.coordinate.longitude,
                    }}
                    zIndex={getMarkerZIndex({ isSelected, hasHiddenIcon })}
                    onClick={() => {
                      const originalMarker = (originalMarkerById.get(
                        marker.id,
                      ) ?? marker) as T;
                      onMarkerPress(originalMarker as any);
                    }}
                  >
                    <MapNode
                      {...mapMarkerToMapNodeProps(
                        marker as any,
                        isSelected,
                        () => {
                          const originalMarker = (originalMarkerById.get(
                            marker.id,
                          ) ?? marker) as T;
                          onMarkerPress(originalMarker as any);
                        },
                      )}
                      hideIcon={!isSelected && hasHiddenIcon}
                    />
                  </AdvancedMarker>
                );
              })}
            </Map>
          </APIProvider>
        ) : null}

        {shouldShowSearchThisAreaButtonVisible &&
        isSearchThisAreaButtonVisible ? (
          <View style={styles.searchThisAreaContainer}>
            <Pressable
              style={styles.searchThisAreaButton}
              onPress={handleSearchThisAreaPress}
            >
              <BodySmBoldText style={styles.searchThisAreaButtonText}>
                {translatedText('searchThisAreaButton')}
              </BodySmBoldText>
            </Pressable>
          </View>
        ) : null}

        <RequestLocationBottomSheet
          onLocation={handleLocation}
          onDeniedLocation={handleDeniedLocation}
          title={requestLocationTitle}
          body={requestLocationBody}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  mapContainer: {
    alignItems: 'center',
    flex: 1,
    height: '100%',
    justifyContent: 'center',
    width: '100%',
    zIndex: -1,
  },
  searchThisAreaButton: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    borderRadius: 40,
    flexDirection: 'row',
    top: theme.spacing.m,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
    shadowColor: 'rgba(0, 0, 0, 0.12)',
    shadowOffset: { height: 2, width: 0 },
    shadowOpacity: 1,
    shadowRadius: 8,
  },
  searchThisAreaButtonText: {
    color: theme.color.interactive.primary,
    textAlign: 'center',
  },
  searchThisAreaContainer: {
    alignItems: 'center',
    left: 0,
    position: 'absolute',
    right: 0,
    top: theme.spacing.m,
  },
});
